import React, {FC, useMemo, useState} from 'react';
import {productPDFSrc, useTranslation} from '$shared/utils';
import {ColumnList} from '$shared/components/column-list';
import {useProduct} from '$shared/utils/product';
import {TabMenu} from '$shared/components/tab-menu';
import {
    HighlightItem,
    HighlightsContent,
    ReadMoreButton,
    StyledDownload,
    StyledDownloadWrapper,
    StyledSpecificationWrapper,
} from './style';
import {Icon} from '$shared/components/icon';
import {ToolTip} from '$shared/components/hover-card-tooltip/tool-tip';
import {ColumnListItemProps} from "$shared/components/column-list/column-list";

export const Specifications: FC = () => {
    const product = useProduct();
    const {translate} = useTranslation();
    const [showAllHighlights, setShowAllHighlights] = useState(false);
    const [showAllSpecifications, setShowAllSpecifications] = useState(false);

    const deduplicateSlashSeparated = (value: string): string => {
        const seen = new Set<string>();
        const parts = value.split('/').map((part) => part.trim());

        const deduped = parts.filter((part) => {
            if (seen.has(part)) return false;
            seen.add(part);
            return true;
        });

        return deduped.join(' / ');
    };

    const renderProductName = (value: string) => {
        const [heading, ...paragraphLines] = value.split(/\r?\n/);

        return (
            <>
                {heading?.trim() && <h2>{heading}</h2>}

                {paragraphLines
                    .filter((line) => line.trim())
                    .map((line, index) => (
                        <p key={index}>{line}</p>
                    ))}
            </>
        );
    };

    const renderLines = (value: string) =>
        value
            .split(/\r?\n/)
            .filter((line) => line.trim())
            .map((line, index) => {
                const startsWithNumber = /^\d+\./.test(line);
                return (
                    <>
                    {startsWithNumber && index > 0 && <br />}
                    <p key={index}>{line}</p>
                    </>
                )
            });

    const SpecificationData = useMemo(() => {
        const propertyDetailRows =
            product?.propertyDetails?.propertyDetails?.map((property) => ({
                dataLabel: property.displayName || property.code,
                dataItem: deduplicateSlashSeparated(property.value || ''),
            })) ?? [];

        const existingSpecificationRows =
            product?.specifications?.map((s) => ({
                dataLabel: (
                    <StyledSpecificationWrapper>
                        {s.name}
                        {s.url && (
                            <StyledDownloadWrapper>
                                <ToolTip
                                    content={translate('generals.details')}
                                    arrow={true}
                                >
                                    <StyledDownload
                                        href={productPDFSrc(s.url)}
                                        target="_blank"
                                    >
                                        <Icon icon="info" color="blue" size={22}/>
                                    </StyledDownload>
                                </ToolTip>
                            </StyledDownloadWrapper>
                        )}
                    </StyledSpecificationWrapper>
                ),
                dataItem: deduplicateSlashSeparated(
                    `${s.value} ${s.suffix || ''}`.trim()
                ),
            })) ?? [];

        return [...propertyDetailRows, ...existingSpecificationRows];
    }, [
        translate,
        product?.propertyDetails?.propertyDetails,
        product?.specifications,
    ]);

    const productSpecificationsByType = useMemo(() => {
        const specsByType: { [type: string]: any[] } = {};

        product?.productSpecifications?.forEach((spec) => {
            if (spec.data) {
                try {
                    const parsedData = JSON.parse(spec.data);

                    if (spec.type && !specsByType[spec.type]) {
                        specsByType[spec.type] = [];
                    }

                    if (spec.type) {
                        specsByType[spec.type].push(parsedData);
                    }
                } catch (error) {
                    console.error(
                        `Error parsing specification data for type ${spec.type}:`,
                        error
                    );
                }
            } else {
                console.warn(`Specification data is undefined for type ${spec.type}`);
            }
        });

        return specsByType;
    }, [product?.productSpecifications]);

    const tabs = useMemo(() => {
        const tabsArray = [
            {
                tabName: translate('product-detail-page.specification-data.specifications'),
                content: (
                    <HighlightsContent>
                        <ColumnList
                            listItems={
                                showAllSpecifications
                                    ? SpecificationData
                                    : SpecificationData.slice(0, 20)
                            }
                        />

                        {SpecificationData.length > 20 && (
                            <ReadMoreButton
                                style={{ marginTop: '10px' }}
                                type="button"
                                aria-expanded={showAllSpecifications}
                                onClick={() =>
                                    setShowAllSpecifications((currentValue) => !currentValue)
                                }
                            >
                                {translate(
                                    showAllSpecifications
                                        ? 'product-detail-page.tabs.read-less'
                                        : 'product-detail-page.tabs.read-more'
                                )}

                                <span aria-hidden="true">
                        <span
                            className={
                                showAllSpecifications
                                    ? 'chevron chevron-up'
                                    : 'chevron'
                            }
                            aria-hidden="true"
                        />
                    </span>
                            </ReadMoreButton>
                        )}
                    </HighlightsContent>
                ),
            },
        ];

        const highlights = product?.highlights;

        if (
            highlights &&
            Object.values(highlights).some(
                (value) => typeof value === 'string' && value.trim()
            )
        ) {
            const visibleHighlights = [
                highlights.productName,
                highlights.taglineSub,
            ].filter((value): value is string => Boolean(value?.trim()));

            const additionalHighlights = [
                highlights.seriesDescription,
                highlights.seriesFeatureLong,
            ].filter((value): value is string => Boolean(value?.trim()));

            tabsArray.push({
                tabName: 'Highlights',
                content: (
                    <HighlightsContent>
                        {visibleHighlights.map((value, index) => (
                            <HighlightItem key={`highlight-${index}`}>
                                {value === highlights.productName
                                    ? renderProductName(value)
                                    : value}
                            </HighlightItem>
                        ))}

                        {showAllHighlights &&
                            additionalHighlights.map((value, index) => (
                                <HighlightItem key={`additional-highlight-${index}`}>
                                    {value === highlights.seriesFeatureLong && (
                                        <h2 className="key-benefits-heading">Key Benefits</h2>
                                    )}

                                    {value === highlights.seriesFeatureShort ||
                                    value === highlights.seriesFeatureLong
                                        ? renderLines(value)
                                        : value}
                                </HighlightItem>
                            ))}

                        {additionalHighlights.length > 0 && (
                            <ReadMoreButton
                                type="button"
                                aria-expanded={showAllHighlights}
                                onClick={() =>
                                    setShowAllHighlights((currentValue) => !currentValue)
                                }
                            >
                                {translate(
                                    showAllHighlights
                                        ? 'product-detail-page.tabs.read-less'
                                        : 'product-detail-page.tabs.read-more'
                                )}

                                <span aria-hidden="true">
                                    <span
                                        className={showAllHighlights ? 'chevron chevron-up' : 'chevron'}
                                        aria-hidden="true"
                                    />
                                </span>
                            </ReadMoreButton>
                        )}
                    </HighlightsContent>
                ),
            });
        }

        Object.entries(productSpecificationsByType).forEach(([type, specs]) => {
            if (type.toLowerCase() === 'iso') {
                return;
            }

            const specItems = specs.map((spec) => {
                return Object.entries(spec).map(([key, value]) => {
                    if (key === 'id') return null;

                    if (
                        typeof value === 'object' &&
                        value !== null &&
                        'value' in value
                    ) {
                        const typedValue = value as {
                            value: string;
                            unit?: string;
                            description?: string;
                        };

                        const dataLabelText = typedValue.description
                            ? `${typedValue.description} (${key})`
                            : key;

                        return {
                            dataLabel: dataLabelText,
                            dataItem: `${typedValue.value} ${typedValue.unit || ''}`.trim() as React.ReactNode,
                        };
                    }

                    return {
                        dataLabel: key,
                        dataItem: String(value) as React.ReactNode,
                    };
                });
            });

            const flattenedSpecItems = specItems.flat();

            tabsArray.push({
                tabName: type,
                content: (
                    <ColumnList
                        listItems={flattenedSpecItems as ColumnListItemProps[]}
                    />
                ),
            });
        });

        return tabsArray;
    }, [
        SpecificationData,
        productSpecificationsByType,
        product?.highlights,
        showAllHighlights,
        showAllSpecifications,
        translate,
    ]);

    if (!tabs.length) return null;
    else return <TabMenu tabs={tabs}/>;
};